/**
 * 仓内作业监控的环节定义、作业类型与设计 token。
 *
 * 配色取自系统主题（src/theme.js 的 colorPrimary #722ed1），与产品门户原型一致。
 * 可见文案全部在 i18n 的 wms.delivery.bagStayMonitor 下，这里只放与语言无关的定义。
 */
import type { BagResult, OpsKind, StageKey } from './types';

export const ALL_STAGES: StageKey[] = [0, 1, 2, 3, 4, 6];
/** 合计只算 ⓿~④，异常包裹单列不计入 */
export const MAIN_STAGES: StageKey[] = [0, 1, 2, 3, 4];

/** ②~④ 的格子有包牌维度 */
export const isBagStage = (s: number): boolean => s >= 2 && s <= 4;

export const STAGE_BY_BAG: Record<Exclude<BagResult, '已发车'>, StageKey> = {
    已装包未发件: 2,
    已发件未装车: 3,
    已装车未发车: 4
};

/** 包牌状态 → i18n key（bagResult.*）。包牌状态是数据契约里的枚举值，展示时翻译 */
export const BAG_RESULT_KEY: Record<BagResult, string> = {
    已装包未发件: 'packed',
    已发件未装车: 'sent',
    已装车未发车: 'loaded',
    已发车: 'departed'
};

/**
 * 后端合并 / 兜底出来的客户名（原型 route.ts 的 OTHER_CUSTOMER / UNKNOWN_CUSTOMER）。
 * 是契约值、也用作筛选条件，展示时翻译成当前语言
 */
export const OTHER_CUSTOMER = '其他客户';
export const UNKNOWN_CUSTOMER = '未知客户';

/** 一个流向在某环节堆了超过这么多包牌，算「包牌堆积」 */
export const PILE_BAGS = 100;

export interface OpsKindDef {
    k: OpsKind;
    color: string;
}

/** 仓内作业类型（对应指标字典「仓内作业」），名称见 i18n opsKinds.* */
export const OPS_KINDS: OpsKindDef[] = [
    { k: 'bigbag', color: '#E8368F' },
    { k: 'in', color: '#14A9A9' },
    { k: 'bag', color: '#722ED1' },
    { k: 'dep', color: '#3491FA' },
    { k: 'load', color: '#FF7D00' },
    { k: 'out', color: '#00A83A' }
];

/** 按作业账号拆分时不含大包到仓量（大包扫描没有账号维度） */
export const ACCT_KINDS = OPS_KINDS.filter((k) => k.k !== 'bigbag') as { k: Exclude<OpsKind, 'bigbag'>; color: string }[];

/** 设计 token。primary 与 src/theme.js 的 colorPrimary 一致。 */
export const C = {
    page: '#F5F6F8',
    border: '#E5E6EB',
    borderLight: '#F2F3F5',
    text1: '#1D2129',
    text2: '#4E5969',
    text3: '#86909C',
    primary: '#722ED1',
    primaryBg: '#F5F0FF',
    level1Bg: '#F7F2FF',
    level1Accent: '#8C4FE8',
    level2Accent: '#D3ADF7',
    danger: '#F53F3F',
    dangerBg: '#FFECE8',
    warn: '#FF7D00',
    warnBg: '#FFF7E8',
    warnText: '#D25F00',
    headBg: '#F7F8FA'
};

export type BatchStatus = '分拣中' | '分拣结束' | '已完成' | '已取消';

export const BATCH_STATUS_STYLE: Record<BatchStatus, { fg: string; bg: string }> = {
    分拣中: { fg: '#722ED1', bg: '#F5F0FF' },
    分拣结束: { fg: '#D25F00', bg: '#FFF1DB' },
    已完成: { fg: '#00A83A', bg: '#E8FFEA' },
    已取消: { fg: '#F53F3F', bg: '#FFECE8' }
};

/** 批次状态 → i18n key（batchStatus.*） */
export const BATCH_STATUS_KEY: Record<BatchStatus, string> = {
    分拣中: 'sorting',
    分拣结束: 'sortingDone',
    已完成: 'completed',
    已取消: 'cancelled'
};
